// (c) 2026 Owl Consultations. All rights reserved.
// Cost trends: platform month-to-month + per-customer month-to-month. SaaS-admin only.
// Lightweight inline SVG charts — no charting dependency.
import { useEffect, useState } from 'react'
import { DollarSign, Loader2, AlertCircle, TrendingUp } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'

const PALETTE = ['#0F766E', '#004E8C', '#CA5010', '#7252AA', '#498205', '#B91C1C', '#0068B8', '#8F6200']
const money = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
const shortMonth = (m: string) => {
  const [y, mo] = m.split('-')
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return mo ? `${names[parseInt(mo)]} ${y?.slice(2)}` : m
}

// Grouped bar chart: platform total (with SQL portion shaded) per month.
function PlatformChart({ months }: { months: { month: string; total: number; sql: number }[] }) {
  const max = Math.max(1, ...months.map(m => m.total))
  const W = 620, H = 200, pad = 34, bw = Math.min(56, (W - pad * 2) / months.length - 14)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img" aria-label="Platform cost by month">
      {[0, 0.5, 1].map(f => {
        const y = pad + (H - pad * 2) * (1 - f)
        return <g key={f}>
          <line x1={pad} y1={y} x2={W - pad} y2={y} stroke="#E4E7EC" strokeWidth={1} />
          <text x={4} y={y + 3} fontSize={9} fill="#98A2B3">{money(max * f)}</text>
        </g>
      })}
      {months.map((m, i) => {
        const x = pad + i * ((W - pad * 2) / months.length) + ((W - pad * 2) / months.length - bw) / 2
        const th = (H - pad * 2) * (m.total / max)
        const sh = (H - pad * 2) * (m.sql / max)
        return <g key={m.month}>
          <rect x={x} y={H - pad - th} width={bw} height={th} rx={3} fill="#CBD5E1" />
          <rect x={x} y={H - pad - sh} width={bw} height={sh} rx={3} fill="#0F766E" />
          <text x={x + bw / 2} y={H - pad + 12} fontSize={9} fill="#667085" textAnchor="middle">{shortMonth(m.month)}</text>
          <text x={x + bw / 2} y={H - pad - th - 4} fontSize={9} fill="#344054" textAnchor="middle" fontWeight={600}>{money(m.total)}</text>
        </g>
      })}
    </svg>
  )
}

// Multi-line chart: one line per customer, month on x.
function CustomerChart({ months, series }: { months: string[]; series: { name: string; series: { month: string; cost: number }[] }[] }) {
  const all = series.flatMap(s => s.series.map(p => p.cost))
  const max = Math.max(1, ...all)
  const W = 620, H = 220, pad = 40
  const xAt = (i: number) => pad + (months.length <= 1 ? 0 : i * (W - pad * 2) / (months.length - 1))
  const yAt = (v: number) => pad + (H - pad * 2) * (1 - v / max)
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img" aria-label="Per-customer cost by month">
        {[0, 0.5, 1].map(f => {
          const y = pad + (H - pad * 2) * (1 - f)
          return <g key={f}>
            <line x1={pad} y1={y} x2={W - pad} y2={y} stroke="#E4E7EC" strokeWidth={1} />
            <text x={4} y={y + 3} fontSize={9} fill="#98A2B3">{money(max * f)}</text>
          </g>
        })}
        {months.map((m, i) => <text key={m} x={xAt(i)} y={H - pad + 14} fontSize={9} fill="#667085" textAnchor="middle">{shortMonth(m)}</text>)}
        {series.map((s, si) => {
          const pts = s.series.map((p, i) => `${xAt(i)},${yAt(p.cost)}`).join(' ')
          const color = PALETTE[si % PALETTE.length]
          return <g key={s.name}>
            <polyline points={pts} fill="none" stroke={color} strokeWidth={2} />
            {s.series.map((p, i) => <circle key={i} cx={xAt(i)} cy={yAt(p.cost)} r={3} fill={color} />)}
          </g>
        })}
      </svg>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
        {series.map((s, si) => (
          <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--color-text-soft)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: PALETTE[si % PALETTE.length] }} />
            {s.name}
          </span>
        ))}
      </div>
    </div>
  )
}

export default function CostTrendsPanel() {
  const [months, setMonths] = useState(6)
  const [platform, setPlatform] = useState<api.CostHistory | null>(null)
  const [byCust, setByCust] = useState<api.CostHistoryByCustomer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void load(months) }, [months])
  async function load(m: number) {
    setLoading(true); setError(null)
    try {
      const [p, c] = await Promise.all([api.getCostHistory(m), api.getCostHistoryByCustomer(m)])
      setPlatform(p); setByCust(c)
    } catch (e: any) { setError(e?.message || 'Failed to load cost trends') }
    finally { setLoading(false) }
  }

  const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, background: 'var(--color-surface)', marginBottom: 16 }
  const custSeries = byCust?.customers ? [...byCust.customers, ...(byCust.shared ? [byCust.shared] : [])] : []

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: 0 }}><TrendingUp size={18} /> Cost trends</h2>
        <div style={{ minWidth: 150 }}>
          <SearchableSelect
            value={String(months)}
            onChange={v => setMonths(parseInt(v))}
            options={[3, 6, 9, 12].map(n => ({ value: String(n), label: `Last ${n} months` }))}
            ariaLabel="Months of cost history"
          />
        </div>
      </div>

      {loading && <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-soft)' }}><Loader2 size={16} className="spin" /> Loading cost data…</div>}
      {error && <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c' }}><AlertCircle size={16} /> {error}</div>}

      {!loading && !error && (
        <>
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <DollarSign size={15} style={{ color: '#0F766E' }} />
              <strong style={{ fontSize: 14 }}>Platform total by month</strong>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)', marginLeft: 6 }}>(teal = database cost, gray = everything else)</span>
            </div>
            {platform?.available && platform.months?.length
              ? <PlatformChart months={platform.months} />
              : <div style={{ color: 'var(--color-text-soft)', fontSize: 13 }}>Cost data unavailable{platform?.reason ? ` — ${platform.reason}` : ''}.</div>}
          </div>

          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <DollarSign size={15} style={{ color: '#004E8C' }} />
              <strong style={{ fontSize: 14 }}>Cost per customer by month</strong>
              <span style={{ fontSize: 12, color: 'var(--color-text-soft)', marginLeft: 6 }}>each customer's dedicated database</span>
            </div>
            {byCust?.available && custSeries.length && byCust.months?.length
              ? <CustomerChart months={byCust.months} series={custSeries} />
              : <div style={{ color: 'var(--color-text-soft)', fontSize: 13 }}>Per-customer data unavailable{byCust?.reason ? ` — ${byCust.reason}` : ''}.</div>}
          </div>
        </>
      )}
    </section>
  )
}
